(ns app.front.municipality
  (:require [app.domain.workspace.model :as workspace-model]
            [app.front.view.system-of-units.neighbour-radius-defaults :as neighbour-subs]
            [app.front.view.workspace.subs :as ws-subs]
            [app.i18n.model :as i18n-model]
            [clojure.set :as set]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))


(rf/reg-sub ::municipality-config
  :-> ::municipality-config)

(rf/reg-sub ::subdivisions+municipalities-raw
  :-> ::subdivisions+municipalities)

(rf/reg-sub ::municipalities-loaded?
  (fn [db _]
    (contains? db ::subdivisions+municipalities)))

(rf/reg-sub ::subdivisions+municipalities
  :<- [::subdivisions+municipalities-raw]
  :<- [:language]
  (fn [[subs+munis language] _]
    (->> subs+munis
         (p/map-vals (fn [munis]
                       (->> munis
                            (map (fn [muni]
                                   (update muni :municipality-name i18n-model/get-text language)))
                            (sort-by :municipality-name)))))))

(rf/reg-sub ::municipalities
  :<- [::subdivisions+municipalities]
  #(->> % vals (apply concat)))


(rf/reg-event-fx ::load-map-config.success
  (fn [{:keys [db]} [_ _ municipality-config-map]]
    (let [gis-config (-> municipality-config-map vals first)
          ;; Make sure we have a token in case of no map layers.
          layers     (conj (:map-layers gis-config) (:neighbour-search-layer gis-config))]
      {:db (update db ::municipality-config p/deep-merge municipality-config-map)
       :fx (map (fn [{:keys [login-url token-id]}]
                  (when (and login-url token-id)
                    [:dispatch [::fetch-token
                                (-> municipality-config-map keys first)
                                login-url
                                token-id]]))
                layers)})))


(rf/reg-event-fx ::load-map-config
  (fn [_ [_ group-id]]
    {:comm/command {:command    (keyword "map-config" group-id)
                    :method     :get
                    :on-success [::load-map-config.success]}}))


(rf/reg-event-fx ::load-municipalities
  (fn [_ [_ remove-hidden? on-success-evt]]
    {:comm/command {:command    :municipality/municipalities
                    :method     :get
                    :on-success [::set-municipalities on-success-evt]
                    :data       (util/mmap remove-hidden?)}}))


(rf/reg-event-fx ::set-municipalities
  (fn [{:keys [db]} [_ on-success-evt _ subdivisions+municipalities]]
    (p/assoc-if {:db (-> (reduce (fn [db municipality]
                                   (update-in db
                                              [::municipality-config (:domain-group-id municipality)]
                                              p/deep-merge municipality))
                                 db
                                 (mapcat second subdivisions+municipalities))
                         (assoc ::subdivisions+municipalities subdivisions+municipalities))}
                :dispatch on-success-evt)))


(rf/reg-event-fx
  ::fetch-token
  (fn [{:keys [db]} [_ municipality login-url token-id]]
    (if (and login-url token-id (not (get-in db [::tokens token-id])))
      {:comm/command {:command    :gis/token
                      :method     :get
                      :data       (util/mmap municipality login-url token-id)
                      :on-success [::token-fetched token-id]}}
      {})))


(rf/reg-event-db
  ::token-fetched
  (fn [db [_ token-id _ {:keys [token]}]]
    (assoc-in db [::tokens token-id] token)))


(rf/reg-sub
  ::map-tokens
  #(-> % ::tokens))


(defn municipal-layers
  ([config tokens municipality-id all-layers?]
   (municipal-layers config tokens municipality-id all-layers? false))
  ([config tokens municipality-id all-layers? keep-row-layers?]
   (let [layers (get-in config [municipality-id :map-layers])]
     (->> (if keep-row-layers?
            layers
            (remove #(contains? (:roles %) :right-of-way-search) layers))
          (keep (fn [{:keys [login-url token-id selected] :or {selected true} :as layer}]
                  ;; map-component needs all layers as it has the layer selector, other maps
                  ;; just the selected layers.
                  (when (or all-layers? selected)
                    (if (and (some? login-url)
                             (some? token-id))
                      ;; Add authentication-requiring layers only after token has been received
                      (when-let [token (get tokens token-id)]
                        (assoc layer :token token))
                      layer))))
          (map #(set/rename-keys % {:property-identifier :propertyIdentifier}))))))


(rf/reg-sub
  ::ws-municipal-layers
  :<- [::municipality-config]
  :<- [::map-tokens]
  :<- [::ws-subs/workspace]
  (fn [[config tokens workspace] [_ keep-row-layers?]]
    (when-let [customer-id (workspace-model/customer-id workspace)]
      (municipal-layers config tokens customer-id true keep-row-layers?))))

(rf/reg-sub
  ::municipal-layers
  :<- [::municipality-config]
  :<- [::map-tokens]
  (fn [[config tokens] [_ municipality-id keep-row-layers?]]
    (municipal-layers config tokens municipality-id false keep-row-layers?)))


(rf/reg-sub
  ::center-coordinates
  :<- [::municipality-config]
  (fn [config [_ municipality-id]]
    (get-in config [municipality-id :map-center])))


(rf/reg-sub
  ::property-identifier
  :<- [::municipality-config]
  (fn [config [_ municipality-id]]
    (get-in config [municipality-id :property-identifier])))


(rf/reg-sub ::default-neighbour-radius
  :<- [::neighbour-subs/default-neighbour-radius]
  (fn [default-radius [_ workspace]]
    (or (some-> workspace :workspace/category-group-config :group.config/neighbour-radius)
        default-radius)))


(defn map-config-loaded?
  "`::municipality-config` is pre-populated with municipality data (see `::set-municipalities`),
   so presence of the group is not enough to tell whether its map config has been loaded.
   `:check-property-owners?` is always part of the map config response."
  [db group-id]
  (-> (get-in db [::municipality-config group-id])
      (contains? :check-property-owners?)))


(defn check-owners? [db group-id feature-property]
  (let [config (get-in db [::municipality-config group-id])]
    (and (-> config :check-property-owners?)
         (-> feature-property :property/integration-id))))


(defn neighbour-layer [config tokens customer-id]
  (when-let [{:keys [login-url token-id] :as layer} (get-in config [customer-id :neighbour-search-layer])]
    (if (and (some? login-url)
             (some? token-id))
      ;; Return authentication-requiring layer only after token has been received
      (when-let [token (get tokens token-id)]
        (assoc layer :token token))
      layer)))


(rf/reg-sub
  ::neighbour-layer
  :<- [::municipality-config]
  :<- [::map-tokens]
  :<- [::ws-subs/workspace]
  (fn [[config tokens workspace] _]
    (let [customer-id (workspace-model/customer-id workspace)
          layer       (neighbour-layer config tokens customer-id)]
      (when layer
        [(assoc layer :id "neighbour-layer")]))))
